/**
 * Plain-language rendering for auto-fix records on the Verification tab.
 *
 * The stored record holds raw before/after values (for J6, a set of counters).
 * A research coordinator should read what CHANGED in words, not a data structure.
 * These helpers turn a record into: a mode-aware lead sentence, a calm badge, and
 * a short list of "was X → Y" lines — never JSON.
 *
 * Wording adapts to mode/status so the tab shows, at a glance, whether a
 * correction is proposed (observe) or done (apply):
 *   observe / would_apply -> "We would correct this automatically." (nothing changed yet)
 *   apply   / applied      -> "We corrected this for you."          (done, reversible)
 *   apply   / failed       -> "We tried … but it didn't take."      (nothing changed)
 */
export interface AutoFixRecordLike {
  checkId: string;
  subject: string;
  description?: string;
  before?: unknown;
  after?: unknown;
  status: "would_apply" | "applied" | "failed" | "skipped";
  mode: "observe" | "apply";
  reason?: string;
}

/** Only records that represent something to show. `skipped` is a no-op — hidden. */
export function isDisplayableAutoFix(r: AutoFixRecordLike): boolean {
  return r.status === "would_apply" || r.status === "applied" || r.status === "failed";
}

/** The section-level framing, derived from the run's mode. */
export function autoFixSectionCopy(mode: "observe" | "apply"): { title: string; blurb: string } {
  return mode === "apply"
    ? {
        title: "Automatic corrections",
        blurb: "The system corrected these for you. Each change was recorded and can be reversed.",
      }
    : {
        title: "Automatic corrections — preview",
        blurb: "These are corrections the system can make for you. It's in preview mode, so nothing has been changed yet.",
      };
}

/** The lead sentence for one record. */
export function autoFixLead(r: AutoFixRecordLike): string {
  switch (r.status) {
    case "applied": return "We corrected this for you.";
    case "would_apply": return "We would correct this automatically.";
    case "failed": return "We tried to correct this automatically, but it didn't take — nothing was changed.";
    default: return "No change was needed.";
  }
}

/** A calm badge (never an alarm colour). */
export function autoFixBadge(r: AutoFixRecordLike): { label: string; tone: "done" | "proposed" | "muted" } {
  switch (r.status) {
    case "applied": return { label: "Corrected", tone: "done" };
    case "would_apply": return { label: "Would correct", tone: "proposed" };
    case "failed": return { label: "Not corrected", tone: "muted" };
    default: return { label: "No change", tone: "muted" };
  }
}

const FIELD_LABELS: Record<string, string> = {
  checkCount: "Number of checks listed",
  categoryBreakdown: "Totals by type",
  severityBreakdown: "Totals by severity",
  // F7 (whole-form dictionary coverage). The dictionary's export file is updated
  // too, but its text is deliberately not stored on the record — it is hundreds of
  // kilobytes — so it never appears here.
  fieldCount: "Number of dictionary rows",
  formCount: "Number of forms in the dictionary",
};

function humanizeKey(k: string): string {
  return FIELD_LABELS[k] || k.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (c) => c.toUpperCase());
}

function isNumberMap(v: unknown): v is Record<string, number> {
  return !!v && typeof v === "object" && !Array.isArray(v) &&
    Object.values(v as Record<string, unknown>).every((x) => typeof x === "number");
}

/**
 * Turn a before/after pair into short, human "was X → Y" lines. Handles the two
 * shapes an auto-fix record uses: a scalar number (e.g. checkCount) and a flat
 * map of numbers (e.g. category/severity breakdowns). Only fields that actually
 * changed are shown.
 */
export function autoFixChangeLines(before: unknown, after: unknown): string[] {
  const b = (before && typeof before === "object" ? before : {}) as Record<string, unknown>;
  const a = (after && typeof after === "object" ? after : {}) as Record<string, unknown>;
  const lines: string[] = [];

  for (const key of Object.keys(a)) {
    const av = a[key];
    const bv = b[key];
    const label = humanizeKey(key);

    if (typeof av === "number") {
      const from = typeof bv === "number" ? bv : 0;
      if (from !== av) lines.push(`${label}: ${from} → ${av}`);
      continue;
    }

    if (isNumberMap(av)) {
      const bMap = isNumberMap(bv) ? bv : {};
      const subKeys = Array.from(new Set([...Object.keys(bMap), ...Object.keys(av)]));
      const parts: string[] = [];
      for (const sk of subKeys) {
        const from = bMap[sk] ?? 0;
        const to = av[sk] ?? 0;
        if (from !== to) parts.push(`${sk} ${from} → ${to}`);
      }
      if (parts.length) lines.push(`${label}: ${parts.join(", ")}`);
      continue;
    }

    // Fallback for any other shape — keep it human, never dump a structure.
    if (JSON.stringify(bv) !== JSON.stringify(av)) lines.push(`${label} updated`);
  }

  return lines;
}
